import React, { useEffect, useRef, useState } from 'react'
import {
    IconButton,
    Typography,
    useTheme,
    CircularProgress,
    Tooltip,
    Snackbar,
    Grid,
} from '@material-ui/core'
import { Box, Alert } from '@mui/material'
import { styled } from '@mui/system'
import { Blanket, BlanketRect, Container } from '../../../components'
import CloseIcon from '@material-ui/icons/Close'
import RefreshIcon from '@material-ui/icons/Refresh'
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary'
import DescriptionIcon from '@material-ui/icons/Description'
import { toMMDDYYYY, toUiTime } from '../../../helpers/date'

import {
    useVideoInspection,
    UnitInspectionVideo,
} from '../../../hooks/useVideoInspection'
import { UnitInspection } from '../../../models'
import { DropFiles } from '../../../components/DropFiles'

interface Props {
    open: boolean
    onClose: () => void
    unitInspectionVideoIds: number[]
    unitInspection: UnitInspection
    rect: BlanketRect
}

export const VideoInspectionBlanket = (props: Props) => {
    const {
        open,
        onClose,
        unitInspectionVideoIds,
        unitInspection,
        rect,
    } = props

    const theme = useTheme()
    const videoRef = useRef<HTMLVideoElement>(null)

    const {
        videos,
        isLoading,
        error,
        refetch,
        uploadVideo,
    } = useVideoInspection(
        unitInspection.id,
        open && unitInspectionVideoIds.length > 0,
    )

    // State
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0)
    const [currentSubtitle, setCurrentSubtitle] = useState('')
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'info' | 'success' | 'warning' | 'error'
    >('info')

    // Current video from the playlist
    const currentVideo: UnitInspectionVideo | undefined =
        videos[selectedVideoIndex]

    // Reset blanket-local playback state when switching to another unit.
    useEffect(() => {
        setSelectedVideoIndex(0)
        setCurrentSubtitle('')
        if (videoRef.current) {
            videoRef.current.currentTime = 0
        }
    }, [unitInspection.id])

    // Guard against stale selected index after video list changes.
    useEffect(() => {
        if (selectedVideoIndex >= videos.length) {
            setSelectedVideoIndex(0)
            setCurrentSubtitle('')
        }
    }, [videos.length, selectedVideoIndex])

    // Handle video time update for subtitles
    const handleTimeUpdate = () => {
        if (!videoRef.current || !currentVideo?.transcripts) {
            setCurrentSubtitle('')
            return
        }

        const currentTime = videoRef.current.currentTime
        const currentTranscript = currentVideo.transcripts.find(
            (t) =>
                t.start !== null &&
                t.end !== null &&
                currentTime >= t.start &&
                currentTime < t.end,
        )
        setCurrentSubtitle(currentTranscript?.transcript ?? '')
    }

    // Handle video selection from playlist
    const handleVideoSelect = (index: number) => {
        setSelectedVideoIndex(index)
        setCurrentSubtitle('')

        if (videoRef.current) {
            videoRef.current.currentTime = 0
        }
    }

    // Handle video upload (now at unit level, not area level)
    const handleDropFiles = (files: File[]) => {
        if (!unitInspection) return

        setSnackbarMessage('Uploading video, please wait...')
        setSnackbarSeverity('info')
        setSnackbarOpen(true)

        // Upload to unit inspection (passing 0 for areaInspectionId since videos are unit-level now)
        uploadVideo(files[0], 0, unitInspection.id)
    }

    // Format video duration
    const formatDuration = (seconds: number | null): string => {
        if (seconds === null) return '--:--'
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const formatUploadedDate = (
        modifiedDate: string | null | undefined,
    ): string => {
        if (!modifiedDate) return 'Unknown upload date'

        const date = new Date(modifiedDate)
        if (Number.isNaN(date.getTime())) return 'Unknown upload date'

        return `${toMMDDYYYY(date)} ${toUiTime(date)}`
    }

    const handleCloseSnackbar = () => setSnackbarOpen(false)
    const contentPaneMaxHeight = 'calc(100vh - 200px)'

    return (
        <Blanket open={open} rect={rect}>
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: theme.palette.background.paper,
                    flexDirection: 'column',
                    overflow: 'hidden',
                    display: 'flex',
                    boxShadow: theme.shadows[8],
                    padding: theme.spacing(1),
                }}
            >
                {/* Header */}
                <Container
                    style={{
                        flexDirection: 'row',
                        borderBottom: `1px solid ${theme.palette.grey[300]}`,
                        padding: theme.spacing(1, 2),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Video Inspection{' '}
                        {unitInspection?.unit?.name
                            ? `- ${unitInspection.unit.name}`
                            : ''}
                    </Typography>
                    <div style={{ display: 'flex' }}>
                        <Tooltip title="Refresh data">
                            <IconButton onClick={() => refetch()}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </Container>

                {/* Body */}
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        overflow: 'hidden',
                    }}
                >
                    {isLoading ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column',
                            }}
                        >
                            <CircularProgress
                                style={{ color: theme.palette.primary.main }}
                                size={100}
                            />
                            <Typography
                                variant="h6"
                                style={{ marginTop: theme.spacing(2) }}
                            >
                                Loading Videos...
                            </Typography>
                        </div>
                    ) : error ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography color="error">
                                Error loading videos
                            </Typography>
                        </div>
                    ) : (
                        <Grid container>
                            {/* Left Section - Video Player & Playlist */}
                            <Grid item xs={12} md={6}>
                                {/* Video Player */}
                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 0,
                                        maxHeight: contentPaneMaxHeight,
                                        overflow: 'auto',
                                    }}
                                >
                                    {currentVideo ? (
                                        <VideoContainer>
                                            <VideoTitleBar>
                                                <Typography
                                                    style={{ color: 'white' }}
                                                >
                                                    Video{' '}
                                                    {selectedVideoIndex + 1} of{' '}
                                                    {videos.length}
                                                </Typography>
                                            </VideoTitleBar>
                                            <VideoPlayer
                                                ref={videoRef}
                                                controls
                                                src={currentVideo.video}
                                                onTimeUpdate={handleTimeUpdate}
                                            />
                                            {currentSubtitle && (
                                                <SubtitleOverlay>
                                                    {currentSubtitle}
                                                </SubtitleOverlay>
                                            )}
                                        </VideoContainer>
                                    ) : (
                                        <NoVideoContainer>
                                            <DropFiles
                                                onDrop={handleDropFiles}
                                                displayString="Upload a video for this inspection"
                                                fileTypes={{
                                                    'video/*': ['.mp4'],
                                                }}
                                            />
                                        </NoVideoContainer>
                                    )}
                                </div>
                            </Grid>

                            {/* Right Section - Video Playlist & Areas & Inventory Items */}
                            <Grid
                                item
                                xs={12}
                                md={6}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: contentPaneMaxHeight,
                                    overflow: 'auto',
                                    paddingLeft: 8,
                                }}
                            >
                                {/* Video Playlist */}
                                {videos.length > 0 && (
                                    <VideoCardsSection>
                                        <VideoCardsHeader>
                                            <VideoLibraryIcon
                                                style={{
                                                    marginRight: 8,
                                                    fontSize: 20,
                                                }}
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                style={{
                                                    color:
                                                        theme.palette.text
                                                            .primary,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Videos ({videos.length})
                                            </Typography>
                                        </VideoCardsHeader>
                                        <VideoCardsRow>
                                            {videos.map((video, index) => (
                                                <VideoSelectorCard
                                                    key={video.id}
                                                    selected={
                                                        index ===
                                                        selectedVideoIndex
                                                    }
                                                    onClick={() =>
                                                        handleVideoSelect(index)
                                                    }
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        style={{
                                                            fontWeight: 600,
                                                            color: 'inherit',
                                                        }}
                                                    >
                                                        Video {index + 1}
                                                    </Typography>
                                                    <VideoCardMeta>
                                                        <span>Duration</span>
                                                        <span>
                                                            {formatDuration(
                                                                video.duration_seconds,
                                                            )}
                                                        </span>
                                                    </VideoCardMeta>
                                                    <VideoCardMeta>
                                                        <span>Uploaded</span>
                                                        <span>
                                                            {formatUploadedDate(
                                                                video.modified_date,
                                                            )}
                                                        </span>
                                                    </VideoCardMeta>
                                                </VideoSelectorCard>
                                            ))}
                                        </VideoCardsRow>
                                    </VideoCardsSection>
                                )}

                                {/* Video Transcript */}
                                {currentVideo?.transcripts &&
                                    currentVideo.transcripts.length > 0 && (
                                        <TranscriptCard>
                                            <TranscriptCardHeader>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <DescriptionIcon
                                                        style={{
                                                            fontSize: 20,
                                                            color:
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        Transcript
                                                    </Typography>
                                                </Box>
                                                <TranscriptBadge>
                                                    {
                                                        currentVideo.transcripts
                                                            .length
                                                    }{' '}
                                                    segments
                                                </TranscriptBadge>
                                            </TranscriptCardHeader>
                                            <TranscriptCardContent>
                                                <TranscriptScrollArea>
                                                    {currentVideo.transcripts?.map(
                                                        (transcript, index) => {
                                                            const nextTimestamp =
                                                                currentVideo
                                                                    .transcripts?.[
                                                                    index + 1
                                                                ]?.start
                                                            const isActive =
                                                                videoRef.current &&
                                                                transcript.start !==
                                                                    null &&
                                                                videoRef.current
                                                                    .currentTime >=
                                                                    transcript.start &&
                                                                videoRef.current
                                                                    .currentTime <
                                                                    (nextTimestamp ??
                                                                        transcript.start +
                                                                            60)

                                                            return (
                                                                <TranscriptSegmentButton
                                                                    key={index}
                                                                    active={
                                                                        isActive ||
                                                                        false
                                                                    }
                                                                    onClick={() => {
                                                                        if (
                                                                            videoRef.current &&
                                                                            transcript.start !==
                                                                                null
                                                                        ) {
                                                                            videoRef.current.currentTime =
                                                                                transcript.start
                                                                            videoRef.current.play()
                                                                        }
                                                                    }}
                                                                >
                                                                    <TranscriptSegmentRow>
                                                                        <TranscriptTimestamp>
                                                                            {formatDuration(
                                                                                transcript.start,
                                                                            )}
                                                                        </TranscriptTimestamp>
                                                                        <TranscriptTextInline>
                                                                            {
                                                                                transcript.transcript
                                                                            }
                                                                        </TranscriptTextInline>
                                                                    </TranscriptSegmentRow>
                                                                </TranscriptSegmentButton>
                                                            )
                                                        },
                                                    )}
                                                </TranscriptScrollArea>
                                            </TranscriptCardContent>
                                        </TranscriptCard>
                                    )}
                            </Grid>
                        </Grid>
                    )}
                </div>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </div>
        </Blanket>
    )
}

const VideoContainer = styled(Box)({
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#000',
})

const VideoTitleBar = styled(Box)({
    padding: '8px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
})

const VideoPlayer = styled('video')({
    width: '100%',
    flex: 1,
    objectFit: 'contain',
    backgroundColor: '#000',
    minHeight: 0,
})

const NoVideoContainer = styled(Box)({
    width: '100%',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#2a2a2a',
    borderRadius: '8px',
})

const VideoCardsSection = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette?.background?.paper ?? '#fff',
    borderRadius: '10px',
    marginBottom: '8px',
    border: `1px solid ${theme.palette?.divider ?? '#e0e0e0'}`,
    overflow: 'hidden',
    flexShrink: 0,
}))

const VideoCardsHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: `1px solid ${theme.palette?.divider ?? '#e0e0e0'}`,
}))

const VideoCardsRow = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    overflowX: 'auto',
    padding: '10px',
    '&::-webkit-scrollbar': {
        height: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(0,0,0,0.25)',
        borderRadius: '4px',
    },
})

const VideoSelectorCard = styled('button')<{ selected?: boolean }>(
    ({ selected, theme }) => ({
        minWidth: '190px',
        maxWidth: '190px',
        borderRadius: '8px',
        border: `1px solid ${
            selected
                ? theme.palette?.primary?.main ?? '#1976d2'
                : theme.palette?.divider ?? '#e0e0e0'
        }`,
        background: selected
            ? 'rgba(25, 118, 210, 0.08)'
            : theme.palette?.background?.default ?? '#fafafa',
        color: theme.palette?.text?.primary ?? '#111',
        textAlign: 'left',
        padding: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: selected
                ? 'rgba(25, 118, 210, 0.14)'
                : 'rgba(0, 0, 0, 0.04)',
        },
    }),
)

const VideoCardMeta = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: '6px',
    gap: '2px',
    '& span:first-of-type': {
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette?.text?.secondary ?? '#666',
    },
    '& span:last-of-type': {
        fontSize: '0.78rem',
        color: theme.palette?.text?.primary ?? '#222',
        lineHeight: 1.2,
        wordBreak: 'break-word',
    },
}))

const SubtitleOverlay = styled('div')({
    position: 'absolute',
    bottom: '40px',
    left: 0,
    width: '100%',
    textAlign: 'center',
    color: 'white',
    background: 'rgba(0,0,0,0.6)',
    fontSize: '1.3rem',
    padding: '8px 0',
    zIndex: 2,
    borderRadius: '6px',
    pointerEvents: 'none',
})

const TranscriptCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette?.background?.paper ?? '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    border: `1px solid ${theme.palette?.divider ?? '#e0e0e0'}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
}))

const TranscriptCardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette?.divider ?? '#e0e0e0'}`,
    flexShrink: 0,
}))

const TranscriptBadge = styled('span')(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette?.text?.secondary ?? '#666',
    fontWeight: 400,
}))

const TranscriptCardContent = styled(Box)({
    flex: 1,
    overflow: 'hidden',
    padding: '8px',
})

const TranscriptScrollArea = styled(Box)({
    height: '100%',
    maxHeight: '500px',
    overflowY: 'auto' as const,
    paddingRight: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(0,0,0,0.3)',
    },
})

const TranscriptSegmentButton = styled('button')<{ active?: boolean }>(
    ({ active, theme }) => ({
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        borderRadius: '6px',
        cursor: 'pointer',
        border: active
            ? `1px solid rgba(25, 118, 210, 0.3)`
            : `1px solid ${theme.palette?.divider ?? '#e0e0e0'}`,
        backgroundColor: active
            ? 'rgba(25, 118, 210, 0.05)'
            : theme.palette?.background?.default ?? '#fafafa',
        boxShadow: active ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: active
                ? 'rgba(25, 118, 210, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
        },
    }),
)

const TranscriptSegmentRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
})

const TranscriptTimestamp = styled('span')(({ theme }) => ({
    fontSize: '0.72rem',
    fontFamily: 'monospace',
    fontWeight: 500,
    color: theme.palette?.primary?.main ?? '#1976d2',
    minWidth: '44px',
    flexShrink: 0,
}))

const TranscriptTextInline = styled('span')(({ theme }) => ({
    fontSize: '0.875rem',
    lineHeight: 1.25,
    color: theme.palette?.text?.primary ?? '#333',
    margin: 0,
}))
